import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Chip, Box, Divider
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

interface UploadPreviewDialogProps {
  open: boolean;
  preview: { add: number[]; update: number[]; delete: number[]; locked: number[] };
  onConfirm: () => void;
  onCancel: () => void;
}

const UploadPreviewDialog = ({ open, preview, onConfirm, onCancel }: UploadPreviewDialogProps) => {
  const sections = [
    {
      label: "Delete",
      ids: preview.delete,
      icon: <DeleteOutlineIcon fontSize="small" />,
      color: "#d32f2f",
      chipColor: "error" as const,
    },
    {
      label: "Update",
      ids: preview.update,
      icon: <EditOutlinedIcon fontSize="small" />,
      color: "#ed6c02",
      chipColor: "warning" as const,
    },
    {
      label: "Add",
      ids: preview.add,
      icon: <AddCircleOutlineIcon fontSize="small" />,
      color: "#2e7d32",
      chipColor: "success" as const,
    },
    {
      label: "Locked — Audio Already Uploaded (No Changes)",
      ids: preview.locked,
      icon: <LockOutlinedIcon fontSize="small" />,
      color: "#757575",
      chipColor: "default" as const,
    },
  ];

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          Songs List Upload: Import Preview
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Review what will change before confirming. Locked songs have audio already uploaded and will not be modified.
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        {sections.map(({ label, ids, icon, color, chipColor }) => (
          <Box key={label} mb={2.5}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Box color={color} display="flex">{icon}</Box>
              <Typography variant="subtitle2" fontWeight={600} color={color}>
                {label} ({ids.length})
              </Typography>
            </Box>

            {ids.length === 0 ? (
              <Typography variant="body2" color="text.disabled" ml={0.5}>
                None
              </Typography>
            ) : (
              <Box display="flex" flexWrap="wrap" gap={0.8}>
                {ids.map(id => (
                  <Chip
                    key={id}
                    label={`#${id}`}
                    size="small"
                    color={chipColor}
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Box>
        ))}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" color="inherit">
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color="primary">
          Yes, Proceed
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadPreviewDialog;